import {
  type AwsPartition,
  isAwsPartition,
} from "@p0security/integrations-aws-shared/util";

export type SqsQueueArn = {
  partition: AwsPartition;
  region: string;
  accountId: string;
  name: string;
};

const QUEUE_ARN_PATTERN =
  /^arn:([a-z-]+):sqs:([a-z0-9-]+):(\d{12}):([A-Za-z0-9_-]{1,75}(?:\.fifo)?)$/;

/** Parses an SQS queue ARN, or returns undefined if it isn't one */
export const parseQueueArn = (arn: string): SqsQueueArn | undefined => {
  const match = QUEUE_ARN_PATTERN.exec(arn.trim());
  if (!match) return undefined;
  const [, partition, region, accountId, name] = match;
  if (!isAwsPartition(partition)) return undefined;
  return { partition, region, accountId, name };
};

export const isFifoQueue = ({ name }: Pick<SqsQueueArn, "name">) =>
  name.endsWith(".fifo");

/** The queue URL for a queue ARN; both supported partitions use amazonaws.com */
export const queueUrl = ({ region, accountId, name }: SqsQueueArn) =>
  `https://sqs.${region}.amazonaws.com/${accountId}/${name}`;

export const QUEUE_ARN_VALIDATION_ERROR_MSG =
  "Queue ARN should have the form `arn:aws:sqs:<region>:<account-id>:<queue-name>`";

export const KMS_KEY_ARN_VALIDATION_ERROR_MSG =
  "KMS key ARN should have the form `arn:aws:kms:<region>:<account-id>:key/<key-id>`";

const KMS_KEY_ARN_PATTERN =
  /^arn:([a-z-]+):kms:[a-z0-9-]+:\d{12}:key\/[A-Za-z0-9-]+$/;

export const isKmsKeyArn = (arn: string) => {
  const match = KMS_KEY_ARN_PATTERN.exec(arn.trim());
  return !!match && isAwsPartition(match[1]);
};
